import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { Row, Col, Spin, Input, Button, Modal, Checkbox } from 'antd'
import { getClinicsService, updateClinicService } from '../../../redux/systemInfos/systemInfosServices';
import { DvIntlMessages, DvNotification } from '../../../components/utility/dvGeneralUtilities';
import { getModalStyle, getModalBodyStyle, DEBOUNCE_TIME_SHORT } from '../../../helpers/utility';
import systemActions from '../../../redux/systemInfos/actions';

const { getClinicDetails } = systemActions;

class ClinicEditModal extends Component {
    state = {
        modalBodyStyle: undefined,
        isInitializeFormRunning: false,
        isUpdateClinicRunning: false,
        isFormInitialized: false
    };

    labelColumnStyle = { flex: '0 1 200px', color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 };
    valueColumnStyle = { flex: '1 1 300px', minHeight: 32 };
    labelStyle = { margin: 0, minHeight: 32, display: 'flex', alignItems: 'center' };
    validationMessageStyle = { marginLeft: 12, color: '#f5222d', minHeight: 24 };
    existingTitles = [];
    existingEmails = [];

    componentDidUpdate() {
        if (this.props.isClinicEditFormOpen) {
            if (!this.state.isFormInitialized && !this.state.isInitializeFormRunning) this.initializeForm();
            this.setModalBodyStyle();
        }
    }

    async setModalBodyStyle() {
        const modalBodyStyle = await getModalBodyStyle('ClinicEditForm', this.props.appView);
        if (this.props.isClinicEditFormOpen && (!this.state.modalBodyStyle || (this.state.modalBodyStyle.maxHeight !== modalBodyStyle.maxHeight))) this.setState({ modalBodyStyle: modalBodyStyle });
    }

    validateFieldDebounced = debounce((field, value) => {
        this.validateField(field, value);
    }, DEBOUNCE_TIME_SHORT);

    validateField = (field, value) => {
        let validationMessage = undefined;
        let formValidationMessagesField = field + 'ValidationMessage';
        const emailRegEx = /^\w+([.-]?\w+)*$/;

        switch (field) {
            case 'clinicTitle':
                if (!value || value.length === 0) validationMessage = DvIntlMessages('general.title') + DvIntlMessages('general.isRequired');
                else if (this.existingTitles.includes(value)) validationMessage = DvIntlMessages('error.titleIsTaken');
                else if (value && value.length > 255) validationMessage = DvIntlMessages('input.noLongerThan255');
                break;
            case 'countryCode':
                if (!value || value.length === 0) validationMessage = DvIntlMessages('general.countryCode') + DvIntlMessages('general.isRequired');
                else if (value && value.length !== 3) validationMessage = DvIntlMessages('input.exactly3');
                break;
            case 'clinicEmail':
                if (!value || value.length === 0) return;
                else if (value.length > 50) validationMessage = DvIntlMessages('input.noLongerThan50');
                else if (!emailRegEx.test(String(value).toLowerCase())) validationMessage = DvIntlMessages('error.invalidEmail');
                else if (this.existingEmails.includes(value)) validationMessage = DvIntlMessages('error.emailIsTaken');
                break;
            default:
                return;
        }

        this.setState(prevState => ({ formValidationMessages: { ...prevState.formValidationMessages, [formValidationMessagesField]: validationMessage } }));
    }

    hasFormErrors = () => {
        const { formValidationMessages } = this.state;
        if (formValidationMessages) {
            for (const key of Object.keys(formValidationMessages)) {
                if (formValidationMessages[key]) return true;
            }
        }

        return false;
    }

    // Get current clinic attributes, set the state and run the validation checks. 
    // Process all existing clinic emails by removing the "icmd+" and "@oncorecords.com" parts for faster singularity check.
    initializeForm = () => {
        this.setState({ isInitializeFormRunning: true }, () => {
            getClinicsService().then((response) => {
                if (response.status === 1 && response.results && response.results.clinics) {
                    const clinics = response.results.clinics
                    let currentClinic;

                    for (let i = 0; i < clinics.length; i++) {
                        if (clinics[i].id === this.props.clinicId) {
                            currentClinic = clinics[i];
                            continue;
                        } else if (clinics[i].oncoEmailAddress !== null) {
                            const existingEmail = clinics[i].oncoEmailAddress.replace('icmd+', '').replace('@oncorecords.com', '');
                            this.existingEmails.push(existingEmail);
                        }
                        this.existingTitles.push(clinics[i].title);
                    }

                    this.setState({
                        formFields: {
                            clinicTitle: currentClinic.title,
                            countryCode: currentClinic.countryCode,
                            clinicEmail: currentClinic.oncoEmailAddress ? currentClinic.oncoEmailAddress.replace('icmd+', '').replace('@oncorecords.com', '') : null,
                            isEnterpriseProduct: currentClinic.isEnterpriseProduct
                        },
                        formValidationMessages: { clinicTitleValidationMessage: undefined, countryCodeValidationMessage: undefined, clinicEmailValidationMessage: undefined },
                        isFormInitialized: true,
                    }, () => Object.keys(this.state.formFields).forEach(key => this.validateField(key, this.state.formFields[key])));
                } else {
                    DvNotification({
                        type: 'error',
                        titleId: 'clinicPage.clinicDetails.editClinic',
                        messageId: response.errors ? null : 'error.general.failToGetData',
                        messageString: response.errors.join(', ')
                    });
                    this.props.closeClinicEditForm();
                }
                this.setState({ isInitializeFormRunning: false })
            });
        });
    }

    updateClinic = () => {
        this.setState({ isUpdateClinicRunning: true }, () => {
            const valuesToUpdate = {
                title: this.state.formFields.clinicTitle,
                countryCode: this.state.formFields.countryCode,
                oncoEmailAddress: this.state.formFields.clinicEmail ? this.state.formFields.clinicEmail : null,
                isEnterpriseProduct: this.state.formFields.isEnterpriseProduct
            }

            updateClinicService(this.props.clinicId, valuesToUpdate).then((response) => {
                this.setState({ isUpdateClinicRunning: false }, () => {
                    if (!response.status === 1) DvNotification({
                        type: 'error',
                        titleId: 'clinicPage.clinicDetails.editClinic',
                        messageId: 'error.updateClinic'
                    });
                    else {
                        this.props.getClinicDetails(this.props.clinicId);
                    }

                    this.props.closeClinicEditForm();
                });
            });
        })
    }

    getFormContent = () => {
        if (!this.state.formValidationMessages || !this.state.formFields || !this.props.isClinicEditFormOpen) return null;
        let { clinicTitle, countryCode, clinicEmail, isEnterpriseProduct } = this.state.formFields;
        const { clinicTitleValidationMessage, countryCodeValidationMessage, clinicEmailValidationMessage } = this.state.formValidationMessages;

        return <>
            <Spin spinning={this.props.loading > 0 || this.state.isInitializeFormRunning || this.state.isUpdateClinicRunning}>
                {/** clinicTitle */}
                <Row type='flex' align='top'>
                    <Col style={this.labelColumnStyle}>
                        <label style={this.labelStyle}>
                            {DvIntlMessages('general.title')}:
                        </label>
                    </Col>
                    <Col style={this.valueColumnStyle}>
                        <Row>
                            <Input type='text' placeholder={DvIntlMessages('general.title')} value={clinicTitle} maxLength={256}
                                style={{ borderColor: clinicTitleValidationMessage ? 'red' : null, boxShadow: clinicTitleValidationMessage ? '0 0 0 0' : null }}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    this.setState(prevState => ({ formFields: { ...prevState.formFields, clinicTitle: value } }));
                                    this.validateFieldDebounced('clinicTitle', value);
                                }} />
                        </Row>
                        <Row style={this.validationMessageStyle}>{clinicTitleValidationMessage}</Row>
                    </Col>
                </Row>

                {/** countryCode */}
                <Row type='flex' align='top'>
                    <Col style={this.labelColumnStyle}>
                        <label style={this.labelStyle}>
                            {DvIntlMessages('general.countryCode')}:
                        </label>
                    </Col>
                    <Col style={this.valueColumnStyle}>
                        <Row>
                            <Input type='text' placeholder={DvIntlMessages('general.countryCode')} value={countryCode} maxLength={256}
                                style={{ borderColor: countryCodeValidationMessage ? 'red' : null, boxShadow: countryCodeValidationMessage ? '0 0 0 0' : null }}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    this.setState(prevState => ({ formFields: { ...prevState.formFields, countryCode: value } }));
                                    this.validateFieldDebounced('countryCode', value);
                                }} />
                        </Row>
                        <Row style={this.validationMessageStyle}>{countryCodeValidationMessage}</Row>
                    </Col>
                </Row>

                {/** clinicEmail */}
                <Row type='flex' align='top'>
                    <Col style={this.labelColumnStyle}>
                        <label style={this.labelStyle}>
                            {DvIntlMessages('general.email')}:
                        </label>
                    </Col>
                    <Col style={this.valueColumnStyle}>
                        <Row>
                            <Col style={{ display: 'flex', alignItems: 'center' }}>icmd+</Col>
                            <Col style={{ display: 'flex', flex: '1 1' }}>
                                <Input type='text' placeholder={DvIntlMessages('general.email')} value={clinicEmail} maxLength={256}
                                    style={{ borderColor: clinicEmailValidationMessage ? 'red' : null, boxShadow: clinicEmailValidationMessage ? '0 0 0 0' : null }}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        this.setState(prevState => ({ formFields: { ...prevState.formFields, clinicEmail: value } }));
                                        this.validateFieldDebounced('clinicEmail', value);
                                    }} />
                            </Col>
                            <Col style={{ display: 'flex', alignItems: 'center' }}>@oncorecords.com</Col>
                        </Row>
                        <Row style={this.validationMessageStyle}>{clinicEmailValidationMessage}</Row>
                    </Col>
                </Row>

                {/** enterprise product */}
                <Row type='flex' align='top'>
                    <Col style={this.labelColumnStyle}>
                        <label style={this.labelStyle}>
                            {DvIntlMessages('general.enterpriseProduct')}:
                        </label>
                    </Col>
                    <Col style={this.valueColumnStyle}>
                        <Row>
                            <Checkbox checked={isEnterpriseProduct} onChange={(e)=>this.setState(prevState=>({formFields: { ...prevState.formFields, isEnterpriseProduct:e.target.checked }}))}/>
                        </Row>
                    </Col>
                </Row>
            </Spin>
        </>
    }

    getModalFooter = () => {
        return (
            <Row type='flex' justify='end'>
                <Button type='primary' disabled={this.hasFormErrors()} onClick={() => this.updateClinic()}>
                    {DvIntlMessages('general.save')}
                </Button>
                <Button type='danger' onClick={() => this.props.closeClinicEditForm()}>
                    {DvIntlMessages('general.cancel')}
                </Button>
            </Row>
        );
    }

    render() {
        return (
            <Modal className={'ClinicEditForm'}
                title={< span style={{ fontSize: 24 }} > {DvIntlMessages('clinicPage.clinicDetails.editClinic')}</span >}
                width={800}
                style={getModalStyle(this.props.appView)}
                bodyStyle={this.state.modalBodyStyle}
                open={this.props.isClinicEditFormOpen}
                closable={false}
                destroyOnClose={true}
                footer={this.getModalFooter()}
            >
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 400, width: '-webkit-fill-available' }}>
                    {this.getFormContent()}
                </div>
            </Modal>
        );
    }
}

ClinicEditModal.propTypes = {
    appView: PropTypes.string.isRequired,
    windowWidth: PropTypes.number.isRequired,
    windowHeight: PropTypes.number.isRequired,
    clinicId: PropTypes.number.isRequired,
    isClinicEditFormOpen: PropTypes.bool.isRequired,
    closeClinicEditForm: PropTypes.func.isRequired,
    data: PropTypes.object.isRequired,
    getClinicDetails: PropTypes.func.isRequired
};

const mapActionsToProps = {
    getClinicDetails
};

function mapStateToProps(state) {
    return {
        appView: state.App.get('view'),
        windowWidth: state.App.get('appWidth'),
        windowHeight: state.App.get('appHeight'),
        data: state.SystemInfos.get('clinicDetails'),
    };
}

const connectedClinicsPage = connect(mapStateToProps, mapActionsToProps)(ClinicEditModal);
export default connectedClinicsPage;